import { createContext, useMemo, useState } from "react";
import { createTheme, type Theme } from "@mui/material/styles";
import { useSelector } from "react-redux";
import { IColorTokens } from "./types/theme";
import { RootState } from "./store/Index";

// Function to return color tokens based on mode and Redux theme state
export const tokens = (
  mode: "light" | "dark",
  themeState?: RootState["theme"]
): IColorTokens => {
  // If themeState is provided and matches the requested mode, use Redux colors
  if (themeState && themeState.mode === mode) {
    return {
      primary: themeState.primary_colors,
      secondary: themeState.secondary_colors,
      neutral: themeState.neutral_colors,
      grey: themeState.grey_colors || themeState.neutral_colors, // Fallback to neutral if grey is not defined
    };
  }

  // Light mode colors (existing)
  return {
    primary: {
      10: "#C29363",
      50: "#C29363",
      100: "#111827",
      200: "#D1A07740",
      300: "#94A3B8",
      400: "#724E41",
      500: "#D1A077",
      600: "#E5BFA5",
      700: "#000000",
      800: "#f0f4f8",
      900: "#ffffff",
    },
    secondary: {
      10: "#6870fa",
      50: "#6870fa",
      100: "#868dfb",
      200: "#333",
      300: "#666",
      400: "#503629",
      500: "#F7E6D8",
      600: "#F1F5F9",
      700: "#E5BFA5",
      800: "#C9B1A1",
      900: "#E5BFA5",
    },
    neutral: {
      10: "#ff0000",
      50: "#ff0000",
      100: "#4CAF50",
      200: "#F0FDFA",
      300: "#0D9488",
      400: "#64748B",
      500: "#475569",
      600: "#334155",
      700: "#F3F4F6",
      800: "#0F172A",
      900: "#F1F5F9",
    },
    grey: {
      10: "#25d366",
      50: "#4267B2",
      100: "#365899",
      200: "#128C7E",
      300: "#FF0000",
      400: "#CC0000",
      500: "#e0e0e0",
      600: "#000000",
      700: "#000000",
      800: "#000000",
      900: "#000000",
    },
  };
};

// Define the theme settings
export const themeSettings = (
  mode: "light" | "dark",
  themeState?: RootState["theme"]
) => {
  const colors = tokens(mode, themeState);

  return {
    palette: {
      mode: mode,
      primary: {
        main: colors.primary[400],
      },
      secondary: {
        main: colors.secondary[50],
      },
      neutral: {
        main: colors.neutral[500],
      },
      background: {
        default: mode === "dark" ? colors.primary[800] : colors.primary[800],
        paper: mode === "dark" ? colors.primary[50] : colors.primary[900],
      },
      text: {
        primary: mode === "dark" ? colors.neutral[50] : colors.primary[700],
        secondary: mode === "dark" ? colors.neutral[100] : colors.primary[100],
      },
    },
    typography: {
      fontFamily: '"Mulish", sans-serif',
      fontSize: 12,
      h1: {
        fontSize: 40,
        color: mode === "dark" ? colors.neutral[50] : undefined,
      },
      h2: {
        fontSize: 32,
        color: mode === "dark" ? colors.neutral[100] : undefined,
      },
      h3: {
        fontSize: 24,
        color: mode === "dark" ? colors.neutral[100] : undefined,
      },
      h4: {
        fontSize: 20,
        color: mode === "dark" ? colors.neutral[300] : undefined,
      },
      h5: {
        fontSize: 16,
        color: mode === "dark" ? colors.neutral[300] : undefined,
      },
      h6: {
        fontSize: 14,
        color: mode === "dark" ? colors.neutral[400] : undefined,
      },
    },
  };
};

interface ColorModeContextType {
  toggleColorMode: () => void;
  mode: "light" | "dark";
}

// Context with default value
export const ColorModeContext = createContext<ColorModeContextType>({
  toggleColorMode: () => {},
  mode: "light",
});

// Custom hook to manage theme mode with state
export const useMode = (): [Theme, ColorModeContextType] => {
  const [mode, setMode] = useState<"light" | "dark">("light");
  const themeState = useSelector((state: RootState) => state.theme); // Access Redux theme state

  const colorMode = useMemo<ColorModeContextType>(
    () => ({
      toggleColorMode: () => {
        setMode((prevMode) => (prevMode === "light" ? "dark" : "light"));
      },
      mode,
    }),
    [mode]
  );

  const theme = useMemo<Theme>(
    () => createTheme(themeSettings(mode, themeState)),
    [mode, themeState]
  );

  return [theme, colorMode];
};

export type ThemeColors = ReturnType<typeof tokens>;
